/**
 * Orders API Service
 * 
 * This module handles all order-related API calls
 * including order management, approval, and status updates.
 */

import axios from 'axios';
import API_CONFIG, { getAuthHeaders } from './config';

const ordersAPI = {
  /**
   * Get orders with optional filtering
   * @param {string} token - JWT access token
   * @param {Object} params - Query parameters
   * @returns {Promise<Array>} List of orders
   */
  async getOrders(token, params = {}) {
    const queryParams = new URLSearchParams(params);
    const response = await axios.get(`${API_CONFIG.BASE_URL}/api/orders?${queryParams}`, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Get order by ID
   * @param {string} token - JWT access token
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} Order details
   */
  async getOrderById(token, orderId) {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/api/orders/${orderId}`, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Approve or reject an order
   * @param {string} token - JWT access token
   * @param {string} orderId - Order ID
   * @param {Object} approvalData - Approval data
   * @returns {Promise<Object>} Approval response
   */
  async approveOrder(token, orderId, approvalData) {
    const response = await axios.post(`${API_CONFIG.BASE_URL}/api/orders/${orderId}/approve`, approvalData, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Update order status
   * @param {string} token - JWT access token
   * @param {string} orderId - Order ID
   * @param {Object} statusData - Status update data
   * @returns {Promise<Object>} Status update response
   */
  async updateOrderStatus(token, orderId, statusData) {
    const response = await axios.post(`${API_CONFIG.BASE_URL}/api/orders/${orderId}/status`, statusData, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Auto transition order status
   * @param {string} token - JWT access token
   * @param {string} orderId - Order ID
   * @param {Object} transitionData - Transition data
   * @returns {Promise<Object>} Transition response
   */
  async transitionOrderStatus(token, orderId, transitionData) {
    const response = await axios.post(`${API_CONFIG.BASE_URL}/api/orders/${orderId}/transition`, transitionData, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Get valid status transitions for an order
   * @param {string} token - JWT access token
   * @param {string} orderId - Order ID
   * @returns {Promise<Object>} Valid transitions
   */
  async getValidTransitions(token, orderId) {
    const response = await axios.get(`${API_CONFIG.BASE_URL}/api/orders/${orderId}/valid-transitions`, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  },

  /**
   * Update order price
   * @param {string} token - JWT access token
   * @param {string} orderId - Order ID
   * @param {number} price - New price
   * @returns {Promise<Object>} Price update response
   */
  async updateOrderPrice(token, orderId, price) {
    const response = await axios.put(`${API_CONFIG.BASE_URL}/api/orders/${orderId}/price`, {
      price: price
    }, {
      headers: getAuthHeaders(token),
      timeout: API_CONFIG.TIMEOUT
    });
    return response.data;
  }
};

export { ordersAPI };
